import React, { createContext, useContext, useState, useEffect } from 'react';

const AdminContext = createContext();

export const useAdmin = () => {
  const context = useContext(AdminContext);
  if (!context) {
    throw new Error('useAdmin must be used within an AdminProvider');
  }
  return context;
};

export const AdminProvider = ({ children }) => {
  const [isAdmin, setIsAdmin] = useState(true); // Set to true for demo purposes
  const [adminData, setAdminData] = useState({
    quotes: [],
    analytics: {
      totalUsers: 1247,
      totalQuizzes: 156,
      engagementRate: 87
    },
    settings: {
      appName: 'ILM Global',
      maintenanceMode: false,
      registrationEnabled: true,
      pointsPerQuiz: 20
    }
  });

  // Initialize admin data
  useEffect(() => {
    // In a real app, this would fetch from an API
    const initializeAdminData = () => {
      const sampleQuotes = [
        {
          id: 1,
          text: "Education is the most powerful weapon which you can use to change the world.",
          author: "Nelson Mandela",
          category: "education",
          isActive: true,
          createdAt: new Date().toISOString()
        },
        {
          id: 2,
          text: "The function of education is to teach one to think intensively and to think critically.",
          author: "Martin Luther King Jr.",
          category: "education",
          isActive: true,
          createdAt: new Date().toISOString()
        },
        {
          id: 3,
          text: "Leadership is not about being in charge. It is about taking care of those in your charge.",
          author: "Simon Sinek",
          category: "leadership",
          isActive: true,
          createdAt: new Date().toISOString()
        }
      ];

      setAdminData(prev => ({
        ...prev,
        quotes: sampleQuotes
      }));
    };

    initializeAdminData();
  }, []);

  // Admin functions
  const addQuote = (quoteData) => {
    const newQuote = {
      id: Date.now(),
      ...quoteData,
      isActive: true,
      createdAt: new Date().toISOString()
    };

    setAdminData(prev => ({
      ...prev,
      quotes: [...prev.quotes, newQuote]
    }));
  };

  const updateQuote = (id, updates) => {
    setAdminData(prev => ({
      ...prev,
      quotes: prev.quotes.map(quote =>
        quote.id === id ? { ...quote, ...updates } : quote
      )
    }));
  };

  const deleteQuote = (id) => {
    setAdminData(prev => ({
      ...prev,
      quotes: prev.quotes.filter(quote => quote.id !== id)
    }));
  };

  const toggleQuoteStatus = (id) => {
    setAdminData(prev => ({
      ...prev,
      quotes: prev.quotes.map(quote =>
        quote.id === id ? { ...quote, isActive: !quote.isActive } : quote
      )
    }));
  };

  const updateAppSettings = (settings) => {
    setAdminData(prev => ({
      ...prev,
      settings: { ...prev.settings, ...settings }
    }));
  };

  const exportData = (type) => {
    const data = adminData[type] || [];
    const dataStr = JSON.stringify(data, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `ilm-global-${type}-${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const importData = (type, jsonData) => {
    try {
      const data = JSON.parse(jsonData);
      setAdminData(prev => ({
        ...prev,
        [type]: data
      }));
    } catch (error) {
      throw new Error('Invalid JSON format');
    }
  };

  const getActiveQuotes = (category = null) => {
    let quotes = adminData.quotes.filter(quote => quote.isActive);
    if (category) {
      quotes = quotes.filter(quote => quote.category === category);
    }
    return quotes;
  };

  const value = {
    isAdmin,
    adminData,
    addQuote,
    updateQuote,
    deleteQuote,
    toggleQuoteStatus,
    updateAppSettings,
    exportData,
    importData,
    getActiveQuotes
  };

  return (
    <AdminContext.Provider value={value}>
      {children}
    </AdminContext.Provider>
  );
};

export default AdminContext;